import { filter, map, orderBy, toNumber, uniq } from "lodash";
import { useMemo, useState } from "react";
import {
  useScorecardsQuery,
  Scorecard,
} from "@/features/scorecards/graphql/generated";
import { FilterItem } from "@/shared/widgets/FiltersDropdown";
import { usePeople } from "@/features/people/hooks/usePeople";
import { Person } from "@/features/people/graphql/generated";
import { useAccountMembers } from "@/features/workspace";

export type ScorecardsSource = "featured" | "workspace" | "all";

export type ScoreCardFilters = {
  tag?: number[];
  category?: number;
  source?: ScorecardsSource;
  ids?: number[];
};

interface ScorecardWithAuthor extends Scorecard {
  author: Person;
  tag?: Scorecard["tag"];
  category?: Scorecard["category"];
  title?: Scorecard["title"];
}

export const useScorecardList = (filters: ScoreCardFilters) => {
  const [scorecardsSource, setScorecardsSource] = useState<ScorecardsSource>(
    filters?.source || "featured"
  );
  const [search, setSearchText] = useState("");

  const { data, loading } = useScorecardsQuery({
    variables: {
      scorecardIds: uniq(filters.ids?.filter(Boolean)) as any,
    },
  });

  const { accountMembers: people } = useAccountMembers();

  const scorecards = useMemo(() => {
    const listItems = filter(
      data?.scorecards,
      (card): card is NonNullable<typeof card> => {
        if (!card) return false;

        const searchValue = search.toLowerCase();
        const templateName = card.title?.toLowerCase() || "";
        const matchesSearch =
          search.length === 0 || templateName.includes(searchValue);

        const matchesIsGlobal =
          scorecardsSource === "all" ||
          (scorecardsSource === "featured" && card.is_global) ||
          (scorecardsSource === "workspace" && !card.is_global);

        const matchesTags =
          !filters.tag?.length ||
          (!!card.tag?.id && filters.tag.includes(card.tag.id));

        const categoryId = card.category?.id;
        const filterCategory = filters.category;
        const matchesCategories =
          !filterCategory ||
          (categoryId !== undefined && categoryId === Number(filterCategory));

        return (
          matchesSearch && matchesIsGlobal && matchesTags && matchesCategories
        );
      }
    );

    return map(orderBy(listItems, "updated_at", "desc"), (card) => {
      const author = people.find(
        (person: Person) => person?.id === card?.created_by
      );
      return {
        ...card,
        author: {
          id: author?.id || 4,
          name: author?.name || "AssessioAI",
          avatar: author?.avatar || null,
        },
      };
    }) as ScorecardWithAuthor[];
  }, [scorecardsSource, filters, search, people, data?.scorecards]);

  const [tags, categories] = useMemo(() => {
    const tagsMap = new Map<number, FilterItem>();
    const categoriesMap = new Map<number, FilterItem>();

    // Map filter values from the templates data set
    scorecards.forEach((card) => {
      if (card?.tag?.id) {
        const { id } = card.tag;
        if (!tagsMap.has(id) && card.tag.name) {
          tagsMap.set(id, { id, label: card.tag.name, count: 0 });
        }
      }

      if (card?.category?.id) {
        const { id } = card.category;
        if (!categoriesMap.has(id) && card.category.name) {
          categoriesMap.set(id, { id, label: card.category.name, count: 0 });
        }
      }
    });
    // Count occurrences for categories from the templates data set
    scorecards.forEach((template) => {
      if (template?.category?.id) {
        const { id } = template.category;
        if (categoriesMap.has(id)) {
          categoriesMap.get(id)!.count! += 1;
        }
      }
    });

    // Get the selected category from filters
    const selectedCategory = filters.category;

    // Filter templates by selected category
    const filteredTemplates = selectedCategory
      ? scorecards.filter(
          (template) => template?.category?.id === selectedCategory
        )
      : scorecards;

    // Count occurrences for tags from the filtered templates data set
    filteredTemplates.forEach((template) => {
      if (template?.tag?.id) {
        const { id } = template.tag;
        if (tagsMap.has(id)) {
          tagsMap.get(id)!.count! += 1;
        }
      }
    });

    return [Array.from(tagsMap.values()), Array.from(categoriesMap.values())];
  }, [filters, scorecards]);

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const handleSetSource = (source: ScorecardsSource) => {
    setScorecardsSource(source);
  };

  return {
    scorecards,
    tags,
    categories,
    scorecardsSource,
    handleSearch,
    handleSetSource,
    loading,
  };
};
